import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { FeedIcon, MeIcon, PeopleIcon, TopicsIcon } from "./components/Icons";
import { Spinner, useAsync } from "./components/common";
import { FeedTab } from "./tabs/Feed";
import { MeTab } from "./tabs/Me";
import { PeopleTab } from "./tabs/People";
import { TopicsTab } from "./tabs/Topics";
import { initTelegram, tap } from "./telegram";
import { PersonCard } from "./views/PersonCard";
import { ThreadView } from "./views/Thread";

type Tab = "feed" | "topics" | "people" | "me";
/** The navigation stack sits *on top of* the current tab and only ever holds
 *  detail screens; the tab itself is separate state, not a stack entry. */
type Screen = { kind: "thread"; id: number } | { kind: "person"; id: number };

const TABS: { id: Tab; label: string; Icon: () => JSX.Element }[] = [
  { id: "feed", label: "Лента", Icon: FeedIcon },
  { id: "topics", label: "Темы", Icon: TopicsIcon },
  { id: "people", label: "Люди", Icon: PeopleIcon },
  { id: "me", label: "Я", Icon: MeIcon },
];

export function App() {
  const [tab, setTab] = useState<Tab>("me");
  const [stack, setStack] = useState<Screen[]>([]);
  const [, forceRender] = useState(0);

  // Theme, fullscreen and safe-area insets are set on mount and re-applied on
  // every Telegram event; the re-render keeps layout in step with them.
  useEffect(() => initTelegram(() => forceRender((n) => n + 1)), []);

  const push = useCallback((screen: Screen) => {
    tap();
    setStack((s) => [...s, screen]);
    window.scrollTo(0, 0);
  }, []);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);

  const openThread = useCallback((id: number) => push({ kind: "thread", id }), [push]);
  const openPerson = useCallback((id: number) => push({ kind: "person", id }), [push]);

  const top = stack[stack.length - 1];

  return (
    <div className="app">
      {top ? (
        <>
          <button className="back" onClick={pop}>
            ← назад
          </button>
          {top.kind === "thread" ? (
            <ThreadView id={top.id} onOpenPerson={openPerson} />
          ) : (
            <PersonScreen id={top.id} onOpenThread={openThread} />
          )}
        </>
      ) : (
        <>
          {tab === "feed" && <FeedTab onOpenThread={openThread} />}
          {tab === "topics" && <TopicsTab onOpenThread={openThread} />}
          {tab === "people" && (
            <PeopleTab onOpenPerson={openPerson} onOpenThread={openThread} />
          )}
          {tab === "me" && <MeTab onOpenThread={openThread} />}
        </>
      )}

      <nav className="tabbar">
        {TABS.map((t) => (
          <button
            key={t.id}
            aria-current={!top && tab === t.id}
            onClick={() => {
              tap();
              setStack([]);
              setTab(t.id);
              window.scrollTo(0, 0);
            }}
          >
            <t.Icon />
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function PersonScreen({ id, onOpenThread }: { id: number; onOpenThread: (id: number) => void }) {
  const { data, loading, error } = useAsync(() => api.person(id), [id]);
  if (loading) return <Spinner />;
  if (error || !data) return <div className="notice">{error ?? "Участник не найден"}</div>;
  return (
    <PersonCard
      card={data}
      onOpenThread={onOpenThread}
      self={data.is_self}
      eyebrow={data.is_self ? "личное дело" : "участник сообщества"}
    />
  );
}
